import { inject, Injectable } from '@angular/core';
import { firstValueFrom, of, catchError, Observable } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ConfigService } from 'src/config';
import { handleError } from './service.helpers';

export interface ApiResult {
    success: boolean;
    errorMessage?: string;
}

export interface TripCurrentStateEntry {
    tripActorId: string;
    vehicleId: string | null;
    state: number;
    stateName: string;
    lastChangedAt: string | null;
}

export interface GetTripCurrentStateResponse {
    tripCurrentState: TripCurrentStateEntry;
}

export type StepStatus = 'ok' | 'error' | 'skipped';
export interface SignOnOffStep { status: StepStatus; message: string; source?: string | null; }
export interface SignOnFlowResult {
    status: 'ok' | 'error' | 'conflict';
    message: string;
    signOff?: SignOnOffStep;
    signOn?: SignOnOffStep;
}

@Injectable({
    providedIn: 'root'
})
export class WarningsService {

    private readonly _config = inject(ConfigService);
    private readonly _http = inject(HttpClient);

    public async confirmImmobilizeBypass(inventoryNr: string, comment: string) {
        const url = `${this._config.mapApiBaseUrl}/v1/vehicles/${inventoryNr}/immobilizerbypass/confirm`;
        const body = {
            comment,
        };

        await firstValueFrom(this._http.put(url, JSON.stringify(body)).pipe(
            catchError(error => {
                handleError(error);
                return of(undefined);
            })
        ));
    }

    public async confirmTripNotStarted(warningMessageId: string, comment: string) {
        const url = `${this._config.mapApiBaseUrl}/v1/warning/tripNotStartedWarning/${warningMessageId}/confirm`;
        const body = {
            comment,
        };

        await firstValueFrom(this._http.put(url, JSON.stringify(body)).pipe(
            catchError(error => {
                handleError(error);
                return of(undefined);
            })
        ));
    }

    public async confirmOffRouteWarning(warningMessageId: string, comment: string) {
        const url = `${this._config.mapApiBaseUrl}/v1/warning/offRouteWarning/${warningMessageId}/confirm`;
        const body = {
            comment,
        };

        await firstValueFrom(this._http.put(url, JSON.stringify(body)).pipe(
            catchError(error => {
                handleError(error);
                return of(undefined);
            })
        ));
    }

    public async confirmSignOnMissingWarning(warningMessageId: string, comment: string) {
        const url = `${this._config.mapApiBaseUrl}/v1/warning/signOnWarning/${warningMessageId}/confirm`;
        const body = {
            comment,
        };

        await firstValueFrom(this._http.put(url, JSON.stringify(body)).pipe(
            catchError(error => {
                handleError(error);
                return of(undefined);
            })
        ));
    }

    async signOnRequest(warningMessageId: string, hastusRegion: string, routeName: string,
        tripNumber: string, trafficDate: string): Promise<ApiResult> {

        if (!trafficDate || !warningMessageId || !hastusRegion || !routeName || !tripNumber) {
            throw new Error("BadRequest for signOn.");
        }

        const url = `${this._config.mapApiBaseUrl}/v1/warning/signOnWarning/${warningMessageId}/request`;
        const body = {
            trafficDate,
            hastusRegion,
            routeName,
            tripNumber
        };

        const bodyJson = JSON.stringify(body);

        try {
            await firstValueFrom(this._http.put(url, bodyJson));
            return { success: true };
        } catch (error: any) {
            handleError(error);
            const errorMessage = error?.error?.message || error?.message || 'Unknown error';
            return { success: false, errorMessage };
        }
    }

    public async getTripCurrentState(
        hastusRegion: string,
        routeName: string,
        tripNumber: string,
        trafficDate: string
    ): Promise<TripCurrentStateEntry | null> {
        if (!trafficDate || !hastusRegion || !routeName || !tripNumber) {
            throw new Error('BadRequest for getTripCurrentState.');
        }

        const dateOnly = trafficDate.split('T')[0];

        const actorId =
            `${dateOnly}:${hastusRegion}:${routeName}:${tripNumber}`;
        const url =
            `${this._config.mapApiBaseUrl}/v1/warning/signon/trip/` +
            `${encodeURIComponent(actorId)}/current-state`;

        try {
            const response = await firstValueFrom(
                this._http.get<GetTripCurrentStateResponse>(url)
            );

            return response.tripCurrentState;
        } catch (error: any) {
            handleError(error);
            return null;
        }
    }

    public submitSignOnUpdate(payload: {
        inventoryNumber?: string;
        signOff: boolean;
        signOn: boolean;
        vehicleTaskNo: string;
        signOnItem: {
            trafficDate: string,
            hastusRegion: string,
            routeName: string,
            tripNumber: string,
            vehicleActivityId: string,
            stopId: string,
            sequenceNumber: string
        };
        signOffItem: {
            trafficDate: string,
            hastusRegion: string,
            routeName: string,
            tripNumber: string,
        };

    }): Observable<SignOnFlowResult> {
        const url = `${this._config.mapApiBaseUrl}/v1/warning/signon/update`;

        const fallbackResult: SignOnFlowResult = {
            status: 'error',
            message: 'Request failed while updating sign-on status.',
            signOff: { status: 'error', message: 'Sign-off failed.', source: undefined },
            signOn: { status: 'error', message: 'Sign-on failed.', source: undefined }
        };
      
        return this._http.post<SignOnFlowResult>(url, payload).pipe(
            catchError((err: HttpErrorResponse) =>
                of(err.error?.status ? (err.error as SignOnFlowResult) : fallbackResult)),
        );
    }

}
